import * as v from 'valibot';

/**
 * Creates a schema that validates and reports issues while providing a fallback value.
 * Unlike v.fallback(), this preserves validation issues in the result while maintaining required fields.
 *
 * @param schema - The schema to validate against
 * @param fallback - The fallback value to use when validation fails
 * @param message - Optional custom error message (supports @WARNING:/@ERROR: prefix)
 * @returns A schema that validates, reports issues, and provides fallback
 *
 * @example
 * ```ts
 * const schema = v.object({
 *   donorORTimeString: fallbackWithIssue(v.string(), '--:--', '@WARNING:Invalid time string'),
 *   ActiveTrail: fallbackWithIssue(v.array(itemSchema), [], '@WARNING:Invalid trail data'),
 * });
 * ```
 */
// KNOWN BUG (not fixed here — follow-up): despite the name/docstring below,
// this does NOT substitute `fallback` into the parsed value. `v.rawCheck`'s
// `~run` (valibot@1.2.x) unconditionally returns the ORIGINAL dataset it was
// given and discards whatever the action callback returns, so the `{ typed:
// true, value: fallback }` returned in the branch below is dead code — it
// only relabels the issue's severity (WARNING vs default ERROR); the invalid
// raw value is left in place. Full write-up + the local, ACTUALLY-substituting
// replacement (`gradeItem`) live in
// entities/Weather/api/schemas/weatherSnapshotResponse.schema.ts. It is not
// harmless: a field that fails its type check emits the inner schema's own
// unprefixed issue as well as this @WARNING one, so the severity rule
// (`issueSeverity`, shared/api) grades the item ERROR and both REST and the
// live entity-frame path drop it (TMDXNC-500 E016). Any caller that expects a
// substituted value gets the invalid raw input instead.
export const fallbackWithIssue = <
  TInput,
  TOutput,
  TIssue extends v.BaseIssue<unknown>,
>(
  schema: v.BaseSchema<TInput, TOutput, TIssue>,
  fallback: TOutput,
  message?: string,
): v.BaseSchema<TInput, TOutput, TIssue> =>
  v.pipe(
    schema,
    v.rawCheck(({ dataset, addIssue }) => {
      // If the schema validation already failed, add our custom issue and provide fallback
      if (dataset.typed === false) {
        addIssue({
          message: message || '@WARNING:Validation failed, using fallback',
        });
        // Create a new dataset with the fallback value
        return {
          typed: true,
          value: fallback,
        };
      }
      return dataset;
    }),
  ) as v.BaseSchema<TInput, TOutput, TIssue>;
